import { CustomizableWheel } from '@components/CustomizableWheel';
import { color, theme } from '@lib/style/theme';
import { classNames } from '@lib/utils';
import Button from '@material-ui/core/Button/Button';
import Card from '@material-ui/core/Card/Card';
import CardActionArea from '@material-ui/core/CardActionArea/CardActionArea';
import CardContent from '@material-ui/core/CardContent/CardContent';
import CardMedia from '@material-ui/core/CardMedia/CardMedia';
import Icon from '@material-ui/core/Icon/Icon';
import Typography from '@material-ui/core/Typography/Typography';
import { call } from '@packages/functions';
import { Lightbox } from '@packages/lightbox';
import { IF } from '@packages/react/lib/if';
import { transparentize } from 'polished';
import React, { useCallback } from 'react';
import styled from 'styled-components';

export interface SelectableCardProps {
    id: number;
    title?: any;
    onSelect?: any;
    text?: string;
    showImage?: boolean;
    image?: string;
    isActive?: boolean;
    onEdit?: any;
    selectionType?: any;
    showPreview?: boolean;
    previewImages?: [];
    currentIndex?: number;

    /** Design Props **/
    imageHeight?: any;
    width?: any;
    showCustomizableWheel?: boolean;
    border?: string;
    borderRadius?: string;
}

function SelectableCard(props: SelectableCardProps) {
    const definedSet = props.previewImages && props.previewImages.length > 0;

    const handleEdit = useCallback(() => {
        call(props.onEdit, props.id);
    }, [props.onEdit, props.id]);

    return (
        <StyledCard
            className={classNames('card', { active: props.isActive })}
            imageHeight={props.imageHeight}
            width={props.width}
            border={props.border}
            borderRadius={props.borderRadius}
        >
            {IF(props.showCustomizableWheel, <CustomizableWheel />)}
            <CardActionArea onClick={() => props.onSelect(props.id, props.title)} className={'card-action-area'}>
                {props.showImage && props.image ? (
                    <CardMedia className={'media'} image={props.image ?? ''} title={props.title ?? ''} />
                ) : props.showImage && !props.image ? (
                    <div title={props.title} className={'media default'} />
                ) : null}
                {props.title || props.text || props.showPreview ? (
                    <CardContent className={classNames('card-content', { active: props.isActive })}>
                        {typeof props.title === 'string' ? (
                            <Typography variant="h2" className={'card-content-title'}>
                                {props.title}
                            </Typography>
                        ) : (
                            props.title
                        )}

                        {props.text ? (
                            <Typography component="p" className={'card-content-text'}>
                                {props.text}
                            </Typography>
                        ) : null}
                        {props.showPreview === true ? (
                            <Lightbox
                                images={definedSet ? props.previewImages : [props.image]}
                                showThumbnails={definedSet}
                                showImageCount={definedSet}
                            >
                                <Typography
                                    variant={'button'}
                                    component={'div'}
                                    className={'card-content-text preview'}
                                    {...{ index: props.currentIndex }}
                                >
                                    Preview
                                </Typography>
                            </Lightbox>
                        ) : null}
                    </CardContent>
                ) : null}
            </CardActionArea>
            {typeof props.onEdit === 'function' ? (
                <Button onClick={handleEdit} className={'edit-button'}>
                    <Icon>edit</Icon>
                </Button>
            ) : null}
        </StyledCard>
    );
}

const StyledCard = styled(Card)<any>`
    && {
        width: ${(props) => (props.width ? props.width : '100%')};
        position: relative;
        border: ${(props) => (props.border ? props.border : '1px solid #bbb')};
        border-radius: ${(props) => (props.borderRadius ? props.borderRadius : '0')};
        padding: 1px;
        background-color: #fff;
        box-shadow: none;

        &.active {
            border: 2px solid ${theme('colors.primary')};
            box-shadow: 0 0 5px ${theme('colors.primary', '#000', (c) => transparentize(0.7, c))};
            padding: 0;
        }

        .card-action-area {
            height: 100%;
        }

        .card-content {
            text-align: center;

            &-title {
                font-size: 16px;
                color: #000;
                font-weight: 500;
                margin-bottom: 5px;
            }

            &-text {
                color: #555;
                white-space: pre-line;
            }
            .preview {
                padding: 10px 20px;
                margin-top: 20px;
                color: #fff;
                font-size: 0.9em;
                background-color: ${color('primary')};
                display: inline-block;
                border-radius: 0.25rem;
                opacity: 0;
                visibility: hidden;
                box-shadow: 0 3px 5px rgba(0, 0, 0, 0.2);
                transition: all 0.4s ease;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translateY(-50%) translateX(-50%);
            }
        }
        .media {
            height: ${(props) => (props.imageHeight ? props.imageHeight : '200px')};
            background-size: contain !important;

            &.default {
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: #fff;
            }

            @media screen and (max-width: 600px) {
                & {
                    height: 120px;
                }
            }
        }

        .edit-button {
            position: absolute;
            top: 0;
            right: 0;
        }

        &:hover {
            border: 2px solid ${theme('colors.primary')};
            box-shadow: 0 0 5px ${theme('colors.primary', '#000', (c) => transparentize(0.7, c))};
            padding: 0;

            .preview {
                opacity: 1;
                visibility: visible;
                margin-top: 0;
            }
        }
    }
`;

export default SelectableCard;
